import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { API_URL } from '../api/config';
import {
    UserCircleIcon,
    EnvelopeIcon,
    PhoneIcon,
    MapPinIcon,
    ShieldCheckIcon,
    GlobeAltIcon,
    CameraIcon,
    CheckCircleIcon,
    ExclamationTriangleIcon,
    PencilIcon,
    ArrowPathIcon,
    XMarkIcon
} from '@heroicons/react/24/outline';

const ProfilePage: React.FC = () => {
    const { user, updateProfile } = useAuth();
    const { t } = useTranslation();
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState({
        full_name: '',
        email: '',
        phone: '',
        location: '',
        language: 'en'
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

    // Profile photo upload state
    const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
    const [photoPreview, setPhotoPreview] = useState<string | null>(null);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);

    useEffect(() => {
        if (user) {
            setFormData({
                full_name: user.full_name || '',
                email: user.email || '',
                phone: (user as any).phone || '',
                location: (user as any).location || '',
                language: user.language || 'en'
            });
        }
    }, [user]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        setMessage(null);

        try {
            await updateProfile(formData);
            setMessage({ type: 'success', text: 'Profile updated successfully!' });
            setIsEditing(false);
        } catch (error: any) {
            setMessage({ type: 'error', text: error.message || 'Failed to update profile' });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                setMessage({ type: 'error', text: 'File size must be less than 5MB' });
                return;
            }
            if (!['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(file.type)) {
                setMessage({ type: 'error', text: 'Only JPEG, PNG, and WebP images are allowed' });
                return;
            }
            setSelectedFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setPhotoPreview(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const handlePhotoUpload = async () => {
        if (!selectedFile) return;

        setIsUploadingPhoto(true);
        setMessage(null);

        try {
            const formData = new FormData();
            formData.append('photo', selectedFile);

            const token = localStorage.getItem('aaywa_token');
            const response = await fetch(`${API_URL}/api/users/profile-photo`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: formData
            });

            if (!response.ok) throw new Error('Upload failed');

            const data = await response.json();
            setMessage({ type: 'success', text: 'Profile photo updated successfully!' });
            setSelectedFile(null);
            setPhotoPreview(null);

            // Reload to fetch updated profile
            window.location.reload();
        } catch (error) {
            setMessage({ type: 'error', text: 'Failed to upload photo. Please try again.' });
        } finally {
            setIsUploadingPhoto(false);
        }
    };

    if (!user) return <div className="p-8">Loading profile...</div>;

    const displayPhoto = photoPreview || (user as any).profile_photo_url
        ? `${photoPreview || API_URL}${(user as any).profile_photo_url}`
        : null;
    const languageLabel = user.language === 'fr' ? 'Français' : user.language === 'rw' ? 'Kinyarwanda' : 'English';

    const infoRows = [
        { icon: EnvelopeIcon, label: 'Email Address', value: user.email },
        { icon: PhoneIcon, label: 'Phone Number', value: (user as any).phone || 'Not provided' },
        { icon: MapPinIcon, label: 'Location', value: (user as any).location || 'Kigali, Rwanda' },
        { icon: ShieldCheckIcon, label: 'Role', value: t(`roles.${user.role}`, user.role) },
        { icon: GlobeAltIcon, label: 'Language', value: languageLabel },
    ];

    return (
        <div className="max-w-7xl mx-auto space-y-8 pb-20">
            {/* Page header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">{t('profile.title', 'User Profile')}</h1>
                    <p className="text-lg text-slate-500 mt-1">{t('profile.subtitle', 'Manage your personal information and preferences')}</p>
                </div>
                <button
                    onClick={() => setIsEditing(!isEditing)}
                    className={`inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl font-bold text-sm transition-all shadow-sm ${isEditing
                        ? 'bg-slate-200 text-slate-700 hover:bg-slate-300'
                        : 'bg-brand-blue-600 text-white hover:bg-brand-blue-700 shadow-brand-blue-600/20'
                        }`}
                >
                    {isEditing ? <XMarkIcon className="w-4 h-4" /> : <PencilIcon className="w-4 h-4" />}
                    {isEditing ? t('common.cancel', 'Cancel') : t('profile.edit', 'Edit Profile')}
                </button>
            </div>

            {message && (
                <motion.div
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`p-4 rounded-xl border flex items-center ${message.type === 'success' ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-rose-50 border-rose-200 text-rose-800'}`}
                >
                    {message.type === 'success'
                        ? <CheckCircleIcon className="w-5 h-5 mr-3 text-emerald-500" />
                        : <ExclamationTriangleIcon className="w-5 h-5 mr-3 text-rose-500" />}
                    <span className="text-sm font-medium">{message.text}</span>
                    <button onClick={() => setMessage(null)} className="ml-auto text-current opacity-50 hover:opacity-100">×</button>
                </motion.div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
                {/* Profile Card */}
                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    className="lg:col-span-1 bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm"
                >
                    <div className="relative h-28 bg-gradient-to-r from-brand-blue-600 via-brand-green-500 to-brand-gold-400">
                        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_20%,rgba(255,255,255,.25),transparent_45%)]" />
                    </div>
                    <div className="px-6 pb-6 -mt-12 relative">
                        <div className="mb-5 flex items-end">
                            <div className="relative">
                                {displayPhoto ? (
                                    <img
                                        src={displayPhoto}
                                        alt="Profile"
                                        className="w-28 h-28 rounded-2xl border-4 border-white shadow-lg object-cover bg-white"
                                    />
                                ) : (
                                    <div className="w-28 h-28 rounded-2xl border-4 border-white bg-slate-100 flex items-center justify-center text-4xl font-black text-slate-400 shadow-lg">
                                        {user.full_name?.charAt(0).toUpperCase()}
                                    </div>
                                )}
                                <input
                                    type="file"
                                    id="photo-upload"
                                    accept="image/jpeg,image/jpg,image/png,image/webp"
                                    onChange={handlePhotoSelect}
                                    className="hidden"
                                />
                                <label
                                    htmlFor="photo-upload"
                                    className="absolute -bottom-2 -right-2 bg-brand-blue-600 text-white p-2.5 rounded-xl cursor-pointer hover:bg-brand-blue-700 transition shadow-lg border-2 border-white"
                                    title="Change photo"
                                >
                                    <CameraIcon className="w-4 h-4" />
                                </label>
                            </div>
                            {selectedFile && (
                                <div className="ml-4 space-y-2 flex-1">
                                    <button
                                        onClick={handlePhotoUpload}
                                        disabled={isUploadingPhoto}
                                        className="w-full bg-green-600 text-white py-2 px-4 rounded-xl hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition font-semibold text-sm flex items-center justify-center"
                                    >
                                        {isUploadingPhoto ? <ArrowPathIcon className="w-4 h-4 mr-2 animate-spin" /> : null}
                                        {isUploadingPhoto ? 'Uploading...' : 'Upload Photo'}
                                    </button>
                                    <button
                                        onClick={() => {
                                            setSelectedFile(null);
                                            setPhotoPreview(null);
                                        }}
                                        className="w-full bg-slate-200 text-slate-700 py-2 px-4 rounded-xl hover:bg-slate-300 transition font-semibold text-sm"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            )}
                        </div>

                        <h2 className="text-2xl font-extrabold text-slate-900 tracking-tight">{user.full_name}</h2>
                        <span className="inline-flex items-center gap-1.5 mt-1.5 px-3 py-1 rounded-full bg-brand-blue-50 text-brand-blue-700 text-xs font-bold capitalize">
                            <UserCircleIcon className="w-3.5 h-3.5" />
                            {user.role.replace('_', ' ')}
                        </span>

                        <div className="mt-6 space-y-3 pt-5 border-t border-slate-100">
                            {infoRows.slice(0, 3).map((row) => (
                                <div key={row.label} className="flex items-center text-sm text-slate-600">
                                    <span className="p-1.5 bg-slate-50 rounded-lg mr-3">
                                        <row.icon className="w-4 h-4 text-slate-400" />
                                    </span>
                                    <span className="truncate">{row.value}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </motion.div>

                {/* Detailed Info / Edit Form */}
                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: 0.05 }}
                    className="lg:col-span-2 space-y-6"
                >
                    <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
                        <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between">
                            <div>
                                <h3 className="text-lg font-bold text-slate-900">
                                    {isEditing ? t('profile.edit_details', 'Edit Personal Details') : t('profile.details', 'Personal Details')}
                                </h3>
                                <p className="text-sm text-slate-500 mt-0.5">
                                    {isEditing ? 'Update your contact information and preferences below.' : 'Your account information across the workspace.'}
                                </p>
                            </div>
                            {!isEditing && (
                                <span className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-bold">
                                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                                    Account active
                                </span>
                            )}
                        </div>

                        <div className="p-6">
                            {isEditing ? (
                                <form onSubmit={handleSubmit} className="space-y-5">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                        <div>
                                            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Full Name</label>
                                            <input
                                                type="text"
                                                name="full_name"
                                                value={formData.full_name}
                                                onChange={handleInputChange}
                                                className="w-full px-3.5 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue-500 bg-slate-50/50"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Email Address</label>
                                            <input
                                                type="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleInputChange}
                                                className="w-full px-3.5 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue-500 bg-slate-50/50"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Phone Number</label>
                                            <input
                                                type="text"
                                                name="phone"
                                                value={formData.phone}
                                                onChange={handleInputChange}
                                                className="w-full px-3.5 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue-500 bg-slate-50/50"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Preferred Language</label>
                                            <select
                                                name="language"
                                                value={formData.language}
                                                onChange={handleInputChange}
                                                className="w-full px-3.5 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue-500 bg-slate-50/50"
                                            >
                                                <option value="en">English</option>
                                                <option value="fr">Français</option>
                                                <option value="rw">Kinyarwanda</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Location</label>
                                            <input
                                                type="text"
                                                name="location"
                                                value={formData.location}
                                                onChange={handleInputChange}
                                                className="w-full px-3.5 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue-500 bg-slate-50/50"
                                                placeholder="City, Country"
                                            />
                                        </div>
                                    </div>
                                    <div className="pt-3 flex items-center gap-3">
                                        <button
                                            type="submit"
                                            disabled={isSubmitting}
                                            className="inline-flex items-center px-6 py-2.5 bg-brand-blue-600 text-white rounded-xl font-bold hover:bg-brand-blue-700 disabled:opacity-50 transition shadow-sm"
                                        >
                                            {isSubmitting ? <ArrowPathIcon className="w-4 h-4 mr-2 animate-spin" /> : null}
                                            {isSubmitting ? 'Saving...' : 'Save Changes'}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setIsEditing(false)}
                                            className="px-5 py-2.5 text-slate-600 font-semibold hover:bg-slate-50 rounded-xl transition"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </form>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                                    {infoRows.map((row) => (
                                        <div key={row.label} className="flex items-start">
                                            <div className="p-2.5 bg-brand-blue-50 rounded-xl mr-4">
                                                <row.icon className="w-5 h-5 text-brand-blue-600" />
                                            </div>
                                            <div className="min-w-0">
                                                <p className="text-xs text-slate-500 uppercase tracking-wider font-bold">{row.label}</p>
                                                <p className="text-slate-900 font-semibold mt-0.5 break-words">{row.value}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="bg-gradient-to-r from-brand-blue-50 to-brand-green-50 border border-brand-blue-100 rounded-2xl p-6 flex items-start">
                        <div className="p-2.5 bg-white rounded-xl mr-4 shadow-sm">
                            <ShieldCheckIcon className="w-5 h-5 text-brand-green-600" />
                        </div>
                        <div>
                            <h4 className="text-sm font-bold text-slate-900">Account security</h4>
                            <p className="text-sm text-slate-600 mt-1">You can update your password and notification preferences from the Settings page.</p>
                        </div>
                    </div>
                </motion.div>
            </div>
        </div>
    );
};

export default ProfilePage;
